import {
	faCheck,
	faChevronDown,
	faTimes,
} from '@fortawesome/pro-solid-svg-icons';
import {
	createTemplatePreview,
	validateTemplate,
} from '@supply-mover-2-0/core';
import { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Icon } from '../../../components/base/Icon';
import { Button } from '../../../components/forms/Button';
import { useThemeColors } from '../../../providers/ThemeProvider';

interface TemplateBuilderProps {
	value: string;
	onChange: (template: string) => void;
	availableFields: Array<{
		fieldName: string;
		fieldLabel: string;
		objectTable: string;
		objectLabel: string;
	}>;
	onClose?: () => void;
}

export function TemplateBuilder({
	value,
	onChange,
	availableFields,
	onClose,
}: TemplateBuilderProps) {
	const colors = useThemeColors();
	const [template, setTemplate] = useState(value || '');
	const [showFieldDropdown, setShowFieldDropdown] = useState(false);
	const [validationError, setValidationError] = useState<string | null>(null);
	const [cursorPosition, setCursorPosition] = useState(0);
	const inputRef = useRef<HTMLTextAreaElement>(null);
	const dropdownButtonRef = useRef<HTMLButtonElement>(null);

	// Group fields by object
	const fieldsByObject = availableFields.reduce(
		(acc, field) => {
			const bucket = acc[field.objectTable] ?? {
				label: field.objectLabel,
				fields: [],
			};
			acc[field.objectTable] = bucket;
			bucket.fields.push(field);
			return acc;
		},
		{} as Record<string, { label: string; fields: typeof availableFields }>,
	);

	// Validate template on change
	useEffect(() => {
		if (template) {
			const fieldNames = availableFields.map(
				(f) => `${f.objectTable}.${f.fieldName}`,
			);
			const validation = validateTemplate(template, fieldNames);
			setValidationError(validation.isValid ? null : validation.error || null);
		} else {
			setValidationError(null);
		}
	}, [template, availableFields]);

	const handleTemplateChange = (text: string) => {
		setTemplate(text);
	};

	const handleInsertField = (field: {
		fieldName: string;
		objectTable: string;
	}) => {
		const beforeCursor = template.slice(0, cursorPosition);
		const afterCursor = template.slice(cursorPosition);
		const tableFieldName = `${field.objectTable}.${field.fieldName}`;
		const newTemplate = `${beforeCursor}{${tableFieldName}}${afterCursor}`;

		setTemplate(newTemplate);
		setShowFieldDropdown(false);

		// Set cursor position after the inserted field
		const newPosition = cursorPosition + tableFieldName.length + 2; // +2 for {}
		setCursorPosition(newPosition);

		// Focus back on input
		setTimeout(() => {
			if (inputRef.current) {
				inputRef.current.focus();
				inputRef.current.setSelectionRange(newPosition, newPosition);
			}
		}, 100);
	};

	const handleSelectionChange = () => {
		if (inputRef.current) {
			setCursorPosition(inputRef.current.selectionStart || 0);
		}
	};

	const handleSave = () => {
		if (!validationError && template) {
			onChange(template);
			if (onClose) {
				onClose();
			}
		}
	};

	const preview = createTemplatePreview(template, availableFields);

	return (
		<div className={`${colors.classes.bgPrimary} rounded-lg p-4`}>
			{/* Header */}
			<div className="mb-4">
				<h3
					className={`text-lg font-semibold ${colors.classes.textPrimary} mb-1`}
				>
					Template Field
				</h3>
				<p className={`text-sm ${colors.classes.textSecondary}`}>
					Combine multiple fields into a single text value
				</p>
			</div>

			{/* Template Input */}
			<div>
				<div className="flex gap-2 items-start">
					<div className="flex-1">
						<textarea
							ref={inputRef}
							value={template}
							onChange={(e) => handleTemplateChange(e.target.value)}
							onSelect={handleSelectionChange}
							onClick={handleSelectionChange}
							onKeyUp={handleSelectionChange}
							placeholder="Type text and insert fields, e.g., {city}, {state} {zip}"
							rows={3}
							className={`
                w-full px-3 py-2 border rounded-md text-sm
                ${colors.classes.bgPrimary} 
                ${colors.classes.textPrimary}
                placeholder-subtle
                ${
									validationError
										? colors.classes.borderError
										: colors.classes.borderDefault
								}
                ${colors.classes.focusRingPrimary}
                resize-none
              `}
						/>
						{validationError && (
							<p className={`text-xs ${colors.classes.textError} mt-1`}>
								{validationError}
							</p>
						)}
					</div>

					{/* Insert Field Button */}
					<div className="relative">
						<button
							ref={dropdownButtonRef}
							onClick={() => setShowFieldDropdown(!showFieldDropdown)}
							className={`px-3 py-2 border ${colors.classes.borderDefault} rounded-md ${colors.classes.bgPrimary} ${colors.classes.bgPrimaryHover} flex items-center gap-1 text-sm`}
						>
							<span className={colors.classes.textPrimary}>Insert Field</span>
							<Icon
								icon={faChevronDown}
								size="xs"
								color={colors.values.textSecondary}
							/>
						</button>

						{/* Field Dropdown */}
						{showFieldDropdown &&
							dropdownButtonRef.current &&
							ReactDOM.createPortal(
								<div
									className="fixed z-50"
									style={{
										top:
											dropdownButtonRef.current.getBoundingClientRect().bottom +
											4,
										right:
											window.innerWidth -
											dropdownButtonRef.current.getBoundingClientRect().right,
									}}
								>
									<div
										className="fixed inset-0"
										onClick={() => setShowFieldDropdown(false)}
									/>
									<div
										className={`relative w-64 max-h-96 ${colors.classes.bgPrimary} rounded-lg shadow-lg border ${colors.classes.borderDefault} overflow-hidden`}
									>
										<div className="overflow-y-auto max-h-96">
											{Object.entries(fieldsByObject).map(
												([objectTable, group]) => (
													<div key={objectTable}>
														<div
															className={`px-3 py-2 ${colors.classes.bgSecondary} border-b ${colors.classes.borderDefault}`}
														>
															<p
																className={`text-xs font-medium ${colors.classes.textSecondary}`}
															>
																{group.label}
															</p>
														</div>
														{group.fields.map((field) => (
															<button
																key={field.fieldName}
																onClick={() => handleInsertField(field)}
																className={`w-full px-4 py-2.5 text-left ${colors.classes.bgPrimaryHover} border-b ${colors.classes.borderDefault}`}
															>
																<p
																	className={`text-sm ${colors.classes.textPrimary}`}
																>
																	{field.fieldLabel}
																</p>
																<p
																	className={`text-xs ${colors.classes.textSecondary} mt-0.5`}
																>
																	{`{${field.fieldName}}`}
																</p>
															</button>
														))}
													</div>
												),
											)}
										</div>
									</div>
								</div>,
								document.body,
							)}
					</div>
				</div>

				{/* Field List */}
				<div className="mt-2">
					<p className={`text-xs ${colors.classes.textSecondary}`}>
						Available fields:{' '}
						{availableFields.map((f) => `{${f.fieldName}}`).join(', ')}
					</p>
				</div>
			</div>

			{/* Preview */}
			{template && !validationError && (
				<div className={`mt-4 p-3 ${colors.classes.bgSecondary} rounded-md`}>
					<p
						className={`text-xs font-medium ${colors.classes.textSecondary} mb-1`}
					>
						Preview
					</p>
					<p className={`text-sm ${colors.classes.textPrimary}`}>
						{preview || 'No preview available'}
					</p>
				</div>
			)}

			{/* Actions */}
			<div className="mt-4 flex gap-2 justify-end">
				<Button variant="secondary" size="sm" onClick={onClose} icon={faTimes}>
					Cancel
				</Button>
				<Button
					variant="primary"
					size="sm"
					onClick={handleSave}
					disabled={!template || !!validationError}
					icon={faCheck}
				>
					OK
				</Button>
			</div>
		</div>
	);
}
